import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowCounterClockwise, PencilSimple, BellSlash } from "@phosphor-icons/react";
import api from "@/lib/api";
import { EditReasonDialog, SnoozeConfirmDialog, day } from "@/components/analytics/PauseRowDialogs";

const STATUSES = [["active", "Paused now"], ["past", "Past pauses"]];
const TH = "px-2 py-1.5 text-left";
const TD = "px-2 py-1.5 align-top";
const ACT = "inline-flex items-center gap-1 whitespace-nowrap border border-black px-2 py-1 text-[10px] uppercase tracking-widest transition-colors duration-100 hover:bg-black hover:text-white disabled:opacity-50";
const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");

function what(r) {
  if (r.action === "stop_all") return "All their leads stopped (every buyer)";
  if (r.action === "pause_page") return "Page paused for everyone";
  return `Skipping ${r.buyer_name}`;
}

function Person({ p, at }) {
  return (
    <div>
      <div className="font-bold">{p?.name || p?.email || "Unknown"}</div>
      <div className="text-[11px] text-zinc-600">{when(at)}</div>
    </div>
  );
}

function Ended({ r }) {
  if (r.ended_how === "elsewhere") {
    return <div><div className="font-bold">Turned back on elsewhere</div><div className="text-[11px] text-zinc-600">{when(r.ended_at)}</div></div>;
  }
  return <Person p={r.ended_by} at={r.ended_at} />;
}

function RowActions({ r, busy, onUndo, onEdit, onSnooze }) {
  return (
    <div className="flex flex-col items-end gap-1">
      <button type="button" disabled={busy} onClick={() => onUndo(r)} data-testid={`paused-row-${r.id}-undo`} className={ACT}>
        <ArrowCounterClockwise size={11} weight="bold" /> Undo
      </button>
      <button type="button" disabled={busy} onClick={() => onEdit(r)} data-testid={`paused-row-${r.id}-edit`} className={ACT}>
        <PencilSimple size={11} weight="bold" /> {r.note ? "Edit reason" : "Add reason"}
      </button>
      <button type="button" disabled={busy} onClick={() => onSnooze(r)} data-testid={`paused-row-${r.id}-snooze`} className={ACT}>
        <BellSlash size={11} weight="bold" /> Keep paused 30 more days
      </button>
    </div>
  );
}

function Row({ r, past, canManage, busy, onUndo, onEdit, onSnooze }) {
  const snoozed = !past && r.snoozed_until && new Date(r.snoozed_until) > new Date();
  return (
    <tr className="border-b border-zinc-200 last:border-0" data-testid={`paused-row-${r.id}`}>
      <td className={TD}>
        <div className="label-mono">{r.kind === "vendor" ? "Affiliate" : "Funnel"}</div>
        <div className="font-bold" data-testid={`paused-row-${r.id}-name`}>{r.target_name}</div>
      </td>
      <td className={TD} data-testid={`paused-row-${r.id}-what`}>
        {what(r)}
        {r.note && (
          <div className="mt-1 max-w-xs text-[11px] text-zinc-700" data-testid={`paused-row-${r.id}-note`}>
            <span className="font-bold">Reason:</span> {r.note}
            {r.note_shared && <span className="ml-1 bg-zinc-100 px-1 text-[10px] uppercase tracking-widest">Shared with affiliate</span>}
          </div>
        )}
        {!past && r.reminded_at && (
          <div className="mt-1 text-[10px] text-zinc-500" data-testid={`paused-row-${r.id}-reminded`}>Last reminder {when(r.reminded_at)}</div>
        )}
        {snoozed && (
          <div className="mt-1 text-[10px] font-bold text-zinc-700" data-testid={`paused-row-${r.id}-snoozed`}>No reminders until {day(r.snoozed_until)}</div>
        )}
      </td>
      <td className={TD}><Person p={r.paused_by} at={r.paused_at} /></td>
      {past ? <td className={TD} data-testid={`paused-row-${r.id}-ended`}><Ended r={r} /></td> : (
        <td className={`${TD} text-right`}>
          {canManage && <RowActions r={r} busy={busy} onUndo={onUndo} onEdit={onEdit} onSnooze={onSnooze} />}
        </td>
      )}
    </tr>
  );
}

// Every affiliate and funnel paused from the buyer rejections report, with who paused it and Undo.
export function PausedTrafficList() {
  const [status, setStatus] = useState("active");
  const [data, setData] = useState(null);
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(null);
  const [snoozing, setSnoozing] = useState(null);
  const [params, setParams] = useSearchParams();
  const snoozeId = params.get("snooze");
  useEffect(() => {
    setData(null);
    api.get("/_admin/buyer-rejections/pauses", { params: { status } })
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));
  }, [status, reload]);
  useEffect(() => {
    const row = snoozeId && data?.can_manage && data.rows?.find((x) => x.id === snoozeId && x.active);
    if (row) {
      setSnoozing(row);
    }
  }, [snoozeId, data]);
  const closeSnooze = () => {
    setSnoozing(null);
    if (snoozeId) {
      params.delete("snooze");
      setParams(params, { replace: true });
    }
  };
  const run = async (id, fn, okMsg) => {
    setBusy(id);
    try {
      const msg = await fn();
      toast.success(msg || okMsg);
      setReload((n) => n + 1);
      return true;
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't save");
      return false;
    } finally {
      setBusy("");
    }
  };
  const saveNote = async (note, email) => {
    const r = editing;
    const ok = await run(r.id, async () => {
      const { data: out } = await api.patch(`/_admin/buyer-rejections/pauses/${r.id}`, { note, email_affiliate: email });
      return out.emailed ? "Reason saved — the affiliate has been emailed" : "Reason saved";
    });
    if (ok) {
      setEditing(null);
    }
  };
  const confirmSnooze = async () => {
    const r = snoozing;
    const ok = await run(r.id, async () => {
      const { data: out } = await api.post(`/_admin/buyer-rejections/pauses/${r.id}/snooze`);
      return `Kept paused — no reminders until ${day(out.snoozed_until)}`;
    });
    if (ok) {
      closeSnooze();
    }
  };
  const undo = async (r) => {
    setBusy(r.id);
    try {
      await api.post("/_admin/buyer-rejections/unpause", { buyer_id: r.buyer_id, kind: r.kind, id: r.target_id, action: r.action });
      toast.success("Undone — traffic is flowing again");
      setReload((n) => n + 1);
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't undo");
    } finally {
      setBusy("");
    }
  };
  const past = status === "past";
  return (
    <div data-testid="paused-traffic">
      <div className="mb-4 flex flex-wrap gap-1">
        {STATUSES.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setStatus(k)} data-testid={`paused-traffic-status-${k}`}
                  className={`border border-black px-3 py-1.5 text-xs uppercase tracking-widest transition-colors duration-100 ${status === k ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`}>
            {label}
          </button>
        ))}
      </div>
      {!data && <div className="label-mono p-6 text-center">Loading…</div>}
      {data?.error && <div className="text-sm text-red-700" data-testid="paused-traffic-error">Couldn&apos;t load paused traffic.</div>}
      {data && !data.error && !data.rows.length && (
        <div className="brut-card p-6 text-center text-sm text-zinc-600" data-testid="paused-traffic-empty">
          {past ? "No past pauses yet." : "Nothing is paused right now. Use Pause on a red row of the rejections report to stop bad traffic."}
        </div>
      )}
      {data && !data.error && data.rows.length > 0 && (
        <div className="overflow-x-auto border border-black bg-white">
          <table className="w-full min-w-[640px] text-xs" data-testid="paused-traffic-table">
            <thead>
              <tr className="whitespace-nowrap border-b border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
                <th className={TH}>Traffic</th><th className={TH}>What&apos;s paused</th><th className={TH}>Paused by</th>
                <th className={TH}>{past ? "Turned back on" : ""}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <Row key={r.id} r={r} past={past} canManage={data.can_manage} busy={busy === r.id} onUndo={undo}
                     onEdit={setEditing} onSnooze={setSnoozing} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <EditReasonDialog row={editing} busy={!!busy} onClose={() => setEditing(null)} onSave={saveNote} />
      <SnoozeConfirmDialog row={snoozing} busy={!!busy} onClose={closeSnooze} onConfirm={confirmSnooze} />
    </div>
  );
}
